import React, { useContext, useState } from "react";
import * as MicrosoftGraph from "@microsoft/microsoft-graph-types";
import { getContext as GetTeamsContext } from "@microsoft/teams-js";

import {
  Button,
  Datepicker,
  Dropdown,
  TextArea,
} from "@fluentui/react-northstar";
import { Form } from "@fluentui/react-northstar";
import {
  TextChangeCase16Regular,
  CalendarLtr16Regular,
  Person16Regular,
  List16Regular,
} from "@fluentui/react-icons";

import {
  VerifyPersonInDataverse,
  SaveBenefit,
} from "../../../helpers/ProjectService";

import {
  defaultPicklistKey,
  getPicklistKeys,
  onFocusSelectAll,
  validDate,
} from "../projectUtils";
import { PicklistValues } from "../../../objects/models/PicklistValues";

import Benefit from "../../../objects/models/Benefit";
import "../../../CarnaStyles.css";
import { PeoplePicker } from "@microsoft/mgt-react";
import AttachmentsView from "../Attachments";
import { Required } from "../Required";
import { RoleContext } from "../../../RoleWrapper";
import { CharactersRemaining } from "../CharactersRemaining";

interface Props {
  newForm: boolean;
  project: string;
  projectClosed: boolean;
  picklists: PicklistValues;
  benefit?: Benefit;
  closeAction: () => void;
}

const ProjectBenefit: React.FC<Props> = (props: Props) => {
  const projectID = props.project;
  const picklists = props.picklists;
  const isClosed = props.projectClosed;
  const [newForm, setNewForm] = useState<boolean>(props.newForm);
  const [benefit, setBenefit] = useState<Benefit>();
  const [showErrors, setShowErrors] = useState<boolean>(false);
  const [personError, setPersonError] = useState<string>();
  const [themeCss, setTheme] = useState<string | undefined>("hl");
  const [themeLoaded, setThemeLoaded] = useState<boolean>(false);
  const userRole = useContext(RoleContext);

  GetTeamsContext((context) => {
    if (!themeLoaded) {
      setThemeLoaded(true);
      if (context.theme === "dark") {
        setTheme("nv");
      } else {
        setTheme("hl");
      }
    }
  });

  //e is the mouse/keyboard event, ddl is the data we want. Type doesn't work.
  const onChangeDDL = (e: any, ddl: any) => {
    if (ddl !== undefined && benefit) {
      const key = ddl.id;
      const value = ddl.value.key;
      setBenefit({ ...benefit, [key]: value });
    }
  };

  //The component event handler type doesn't work
  const onChangeTextArea = (e: any) => {
    const value = e.target.value;
    if (value !== undefined && benefit) {
      const key = e.target.id;
      setBenefit({ ...benefit, [key]: value });
    }
  };

  //e has the Date info, type doesn't work.
  const onChangeDate = (d: any, e: any) => {
    if (e.value && benefit) {
      const newValue = e.value as Date;
      setBenefit({
        ...benefit,
        initiationDate: newValue,
      });
    }
  };

  //type doesn't work
  const handlePersonChange = async (e: any) => {
    //guard against "undefined" risk. The risk must be loaded.
    if (benefit) {
      const target = e.target.id;
      //have we selected a new user?
      if (e.detail.length > 0) {
        //we first need to verify that the person chosen from Graph IS someone in our databse.
        //some selections are not in our systemuser table, so they're invalid.
        const newPerson = e.detail[0] as MicrosoftGraph.User;
        const dataverseId = await VerifyPersonInDataverse(newPerson.id!);
        //If they were found in our database, then they are valid. Update our risk object.
        if (dataverseId) {
          setBenefit({
            ...benefit,
            businessOwner: newPerson,
            businessOwnerSysID: dataverseId,
            businessOwnerName: newPerson.displayName as string,
            businessOwnerDomainName: undefined,
          });
          setPersonError(undefined);
        } else {
          const cannedMessage =
            " is an external user. Please select someone else.";
          const personError = newPerson.displayName + cannedMessage;
          setBenefit({ ...benefit, businessOwnerSysID: undefined });
          setPersonError(personError);
        }
      } else {
        //we removed the person. Request they choose another.
        setBenefit({
          ...benefit,
          businessOwner: undefined,
          businessOwnerSysID: undefined,
        });
        setPersonError("Please choose a business owner.");
      }
    }
  };

  //no benefit loaded yet. Get it from the props, or create a fresh one.
  if (benefit === undefined) {
    //we have a benefit, load it!
    if (props.benefit !== undefined) {
      setBenefit(props.benefit);
      return <div>Loading form...</div>;
    }
    //no benefit passed in. Must be a new one.
    else {
      if (newForm) {
        var newBenefit = new Benefit();
        newBenefit.projectId = projectID;
        newBenefit.name = "[New benefit]";
        newBenefit.initiationDate = new Date();
        newBenefit.declaration = "";
        newBenefit.performanceMeasure = "";
        newBenefit.currentBaseline = "";
        setBenefit(newBenefit);
        return <div>Creating new form...</div>;
      } else {
        return (
          <div>
            No benefit supplied, but it's not a new form? Did we forget a prop?
          </div>
        );
      }
    }
  }
  //benefit is loaded, let's rock and roll
  else {
    const saveAndClose = async () => {
      if (!validBenefit(benefit)) {
        setShowErrors(true);
      } else {
        setShowErrors(false);
        await SaveBenefit(benefit, projectID, newForm);
        setNewForm(false);
        props.closeAction();
      }
    };

    const closePopup = () => {
      props.closeAction();
    };

    return (
      <div className="carnaForm" data-testid="benefit-form">
        <Form>
          <h1>
            <TextArea
              id="name"
              key="name"
              value={benefit.name}
              onChange={onChangeTextArea}
              className="titleField"
              autoFocus={newForm}
              onFocus={onFocusSelectAll}
              fluid
              rows={1}
              readOnly={!userRole.canEdit || isClosed}
              maxLength={250}
            />
            <CharactersRemaining value={benefit.name} maxLength={250} />
          </h1>
          <div className="errorText">
            {showErrors
              ? !benefit.name
                ? "Name is required"
                : undefined
              : undefined}
          </div>
          <span onClick={closePopup} className="close-button topright">
            X
          </span>
          <div className="carnaRow">
            <div className="carnaColFull">
              <div className="fieldLabel">
                <TextChangeCase16Regular /> Declaration{" "}
                <Required theme={themeCss} />
              </div>
              <TextArea
                id="declaration"
                key="declaration"
                value={benefit.declaration}
                onChange={onChangeTextArea}
                autoFocus={!newForm}
                fluid
                rows={6}
                resize="vertical"
                readOnly={!userRole.canEdit || isClosed}
                maxLength={2000}
              />
              <CharactersRemaining
                value={benefit.declaration}
                maxLength={2000}
              />
              <div className="errorText">
                {showErrors
                  ? !benefit.declaration
                    ? "Declaration is required"
                    : undefined
                  : undefined}
              </div>
            </div>
          </div>
          <div className="carnaRow">
            <div className="carnaColHalf">
              <div className="fieldLabel">
                <Person16Regular /> Business Owner <Required theme={themeCss} />
              </div>
              <PeoplePicker
                id="businessOwner"
                key="businessOwner"
                aria-required
                className={themeCss}
                defaultSelectedUserIds={
                  benefit.businessOwnerAzureID
                    ? [benefit.businessOwnerAzureID]
                    : undefined
                }
                selectionMode="single"
                selectionChanged={handlePersonChange}
                disabled={!userRole.canEdit || isClosed}
              />
              <div className="errorText">
                {personError
                  ? personError
                  : showErrors
                  ? !benefit.businessOwnerAzureID
                    ? "You must select a business owner"
                    : undefined
                  : undefined}
              </div>
            </div>
          </div>
          <div className="carnaRow">
            <div className="carnaColFull">
              <div className="fieldLabel">
                <TextChangeCase16Regular /> Performance Measure{" "}
                <Required theme={themeCss} />
              </div>
              <TextArea
                id="performanceMeasure"
                key="performanceMeasure"
                value={benefit.performanceMeasure}
                onChange={onChangeTextArea}
                fluid
                rows={6}
                resize="vertical"
                readOnly={!userRole.canEdit || isClosed}
                maxLength={2000}
              />
              <CharactersRemaining
                value={benefit.performanceMeasure}
                maxLength={2000}
              />
              <div className="errorText">
                {showErrors
                  ? !benefit.performanceMeasure
                    ? "Performance measure is required"
                    : undefined
                  : undefined}
              </div>
            </div>
          </div>
          <div className="carnaRow">
            <div className="carnaColFull">
              <div className="fieldLabel">
                <TextChangeCase16Regular /> Current Baseline{" "}
                <Required theme={themeCss} />
              </div>
              <TextArea
                id="currentBaseline"
                key="currentBaseline"
                value={benefit.currentBaseline}
                onChange={onChangeTextArea}
                fluid
                rows={6}
                resize="vertical"
                readOnly={!userRole.canEdit || isClosed}
                maxLength={2000}
              />
              <CharactersRemaining
                value={benefit.currentBaseline}
                maxLength={2000}
              />
              <div className="errorText">
                {showErrors
                  ? !benefit.currentBaseline
                    ? "Current Baseline is required"
                    : undefined
                  : undefined}
              </div>
            </div>
          </div>
          <div className="carnaRow">
            <div className="carnaColHalf">
              <div className="fieldLabel">
                <CalendarLtr16Regular /> Benefits Initiation Date{" "}
                <Required theme={themeCss} />
              </div>
              <Datepicker
                id="dueDate"
                key="dueDate"
                selectedDate={new Date(benefit.initiationDate)}
                onDateChange={onChangeDate}
                disabled={!userRole.canEdit || isClosed}
              />
              <div className="errorText">
                {showErrors
                  ? !validDate(benefit.initiationDate)
                    ? "Please enter a valid date."
                    : undefined
                  : undefined}
              </div>
            </div>
            <div className="carnaColHalf">
              <div className="fieldLabel">
                <List16Regular /> Review Cycle <Required theme={themeCss} />
              </div>
              <Dropdown
                id="reviewCycle"
                key="reviewCycle"
                items={getPicklistKeys(picklists, "crd5f_reviewcycle")}
                defaultValue={defaultPicklistKey(
                  picklists,
                  "crd5f_reviewcycle",
                  benefit.reviewCycle
                )}
                placeholder="Select an option..."
                className="ms-Dropdown-caretDown"
                onChange={onChangeDDL}
                disabled={!userRole.canEdit || isClosed}
              />
              <div className="errorText">
                {showErrors
                  ? !benefit.reviewCycle
                    ? "Review Cycle is required"
                    : undefined
                  : undefined}
              </div>
            </div>
          </div>
          {benefit ? (
            <div>
              <AttachmentsView
                project={projectID}
                entity="invoice"
                entityId={benefit.benefitId}
                disabled={newForm}
              />
            </div>
          ) : undefined}
          {userRole.canEdit && !isClosed ? (
            <div style={{ float: "left" }}>
              <Button content="Save Benefit" onClick={saveAndClose} />
              &nbsp;
              <Button content="Cancel" onClick={closePopup} />
            </div>
          ) : (
            <Button content="Close Popup" onClick={closePopup} />
          )}
        </Form>
      </div>
    );
  }
};

function validBenefit(benefit: Benefit): boolean {
  var valid: boolean = false;
  if (
    [
      benefit.name,
      benefit.declaration,
      benefit.businessOwnerSysID,
      benefit.performanceMeasure,
      validDate(benefit.initiationDate),
      benefit.reviewCycle,
    ].every(Boolean)
  ) {
    valid = true;
  }
  return valid;
}

export default ProjectBenefit;
